import React, { useEffect, useState, useCallback } from "react";
import {
  Lock, LogOut, Inbox, FileText, Trash2, RefreshCw, Mail, Phone, Send, CheckCircle2, AlertTriangle, Flame, Sparkles, Star,
} from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const STATUS_COLORS = {
  new: "bg-[#c6a15b]/15 text-[#8a6d2f]",
  contacted: "bg-blue-100 text-blue-700",
  closed: "bg-slate-200 text-slate-600",
  approved: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function Login({ onSuccess }) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("ltr_admin_token", data.token);
      onSuccess();
    } catch (err) {
      toast({ title: "Login failed", description: "Incorrect password. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-8 sm:p-10 shadow-xl ring-1 ring-slate-100">
        <div className="h-14 w-14 rounded-xl bg-[#0b1f3a] flex items-center justify-center">
          <Lock className="h-7 w-7 text-[#c6a15b]" />
        </div>
        <h1 className="font-display font-extrabold text-2xl text-[#0b1f3a] mt-6">Admin Login</h1>
        <p className="mt-2 text-slate-600">Sign in to manage contact messages and applications.</p>
        <button
          type="button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/admin";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          data-testid="admin-google-signin"
          className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-md border border-slate-200 px-6 py-3.5 font-semibold text-[#0b1f3a] hover:bg-slate-50 transition"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 5.1 29.6 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 5.1 29.6 3 24 3 15.9 3 8.9 7.6 6.3 14.7z"/><path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C8.8 41.3 15.8 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.3 36.6 45 30.9 45 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>
        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <div className="h-px flex-1 bg-slate-200" /> or use admin password <div className="h-px flex-1 bg-slate-200" />
        </div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition"
          placeholder="Enter admin password"
        />
        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full rounded-md bg-[#c6a15b] px-6 py-3.5 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(!!localStorage.getItem("ltr_admin_token"));
  const [processingAuth, setProcessingAuth] = useState(
    () => typeof window !== "undefined" && window.location.hash.includes("session_id=")
  );
  const [tab, setTab] = useState("applications");
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, s] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/applications"),
        api.get("/admin/stats"),
      ]);
      setContacts(c.data);
      setApplications(a.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("ltr_admin_token");
        setAuthed(false);
      } else {
        toast({ title: "Failed to load data", description: "Please try again." });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sid) { setProcessingAuth(false); return; }
    api.post("/auth/session", { session_id: sid })
      .then(({ data }) => {
        localStorage.setItem("ltr_admin_token", data.session_token);
        setAuthed(true);
      })
      .catch((err) => {
        const msg = err?.response?.status === 403
          ? "This Google account isn't authorized for admin access."
          : "Google sign-in failed. Please try again.";
        toast({ title: "Access denied", description: msg });
      })
      .finally(() => setProcessingAuth(false));
  }, [toast]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ltr_admin_token");
    setAuthed(false);
  };

  const updateStatus = async (type, id, status) => {
    try {
      await api.patch(`/admin/${type}/${id}`, { status });
      load();
    } catch {
      toast({ title: "Update failed" });
    }
  };

  const remove = async (type, id) => {
    try {
      await api.delete(`/admin/${type}/${id}`);
      load();
      toast({ title: "Deleted" });
    } catch {
      toast({ title: "Delete failed" });
    }
  };

  const sendTestEmail = async () => {
    setEmailTesting(true);
    setEmailResult(null);
    try {
      const { data } = await api.post("/admin/email-test");
      setEmailResult(data);
      toast({
        title: data.ok ? "Test email sent" : "Test email failed",
        description: data.ok ? `Check ${data.config?.notify_email}` : data.detail,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Request failed";
      setEmailResult({ ok: false, detail });
      toast({ title: "Test failed", description: detail });
    } finally {
      setEmailTesting(false);
    }
  };

  if (processingAuth) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-600" data-testid="admin-auth-processing">
          <RefreshCw className="h-5 w-5 animate-spin text-[#c6a15b]" /> Signing you in…
        </div>
      </div>
    );
  }
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const chatLeads = contacts.filter((c) => c.program === "Chat Lead");
  const messages = contacts.filter((c) => c.program !== "Chat Lead");
  const chatLeadsCount = chatLeads.length;
  const messagesCount = messages.length;

  const statCards = stats
    ? [
        { label: "Total Applications", value: stats.applications },
        { label: "New Applications", value: stats.new_applications },
        { label: "Chat Leads", value: chatLeadsCount },
        { label: "Messages", value: messagesCount },
      ]
    : [];

  return (
    <div className="min-h-[80vh] bg-slate-50">
      {/* Top bar */}
      <div className="bg-[#0b1f3a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-white.png" alt="LTR Capital Partners" className="h-9 w-auto object-contain" />
            <div className="hidden sm:block h-8 w-px bg-white/20" />
            <div>
              <div className="font-display font-bold">Admin</div>
              <div className="text-xs text-slate-300">Lead management dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20 transition">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-md bg-[#c6a15b] px-4 py-2 text-sm font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
              <div className="font-display text-3xl font-extrabold text-[#0b1f3a]">{s.value}</div>
              <div className="mt-1 text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Email diagnostics */}
        <div className="mt-6 rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-[#0b1f3a] flex items-center gap-2">
                <Mail className="h-5 w-5 text-[#c6a15b]" /> Email Notifications
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Send a test email to confirm delivery is working in this environment.
              </p>
            </div>
            <button
              onClick={sendTestEmail}
              disabled={emailTesting}
              className="inline-flex items-center gap-2 rounded-md bg-[#c6a15b] px-5 py-2.5 text-sm font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition disabled:opacity-60"
            >
              <Send className={`h-4 w-4 ${emailTesting ? "animate-pulse" : ""}`} />
              {emailTesting ? "Sending..." : "Send Test Email"}
            </button>
          </div>

          {emailResult && (
            <div className={`mt-5 rounded-xl p-4 ring-1 ${emailResult.ok ? "bg-green-50 ring-green-100" : "bg-red-50 ring-red-100"}`}>
              <div className="flex items-start gap-3">
                {emailResult.ok ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <div className={`font-semibold ${emailResult.ok ? "text-green-700" : "text-red-700"}`}>
                    {emailResult.ok ? "Test email sent successfully" : "Test email failed"}
                  </div>
                  {!emailResult.ok && (
                    <div className="mt-1 text-sm text-red-700 break-words">{emailResult.detail}</div>
                  )}
                  {emailResult.config && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                      <div>Active provider: <b className="text-[#0b1f3a]">{emailResult.config.active_provider}</b></div>
                      <div>Sends to: <b className="text-[#0b1f3a]">{emailResult.config.notify_email}</b></div>
                      <div>Resend key: <b className={emailResult.config.resend_key_present ? "text-green-700" : "text-red-700"}>{emailResult.config.resend_key_present ? "present" : "MISSING"}</b></div>
                      <div>SendGrid key: <b className={emailResult.config.sendgrid_key_present ? "text-green-700" : "text-slate-400"}>{emailResult.config.sendgrid_key_present ? "present" : "not set"}</b></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2">
          <button
            onClick={() => setTab("applications")}
            className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition ${tab === "applications" ? "bg-[#0b1f3a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}
          >
            <FileText className="h-4 w-4" /> Applications ({applications.length})
          </button>
          <button
            onClick={() => setTab("chatleads")}
            className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition ${tab === "chatleads" ? "bg-[#0b1f3a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}
          >
            <Flame className="h-4 w-4" /> Chat Leads ({chatLeadsCount})
          </button>
          <button
            onClick={() => setTab("contacts")}
            className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition ${tab === "contacts" ? "bg-[#0b1f3a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}
          >
            <Inbox className="h-4 w-4" /> Messages ({messagesCount})
          </button>
        </div>

        {/* Applications */}
        {tab === "applications" && (
          <div className="mt-6 space-y-4">
            {applications.length === 0 && <EmptyState label="No applications yet." />}
            {applications.map((a) => (
              <div key={a.id} className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display font-bold text-lg text-[#0b1f3a]">{a.firstName} {a.lastName}</h3>
                      <StatusBadge status={a.status} />
                      {a.priority && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700">
                          <Star className="h-3.5 w-3.5 fill-red-700" /> High Value
                        </span>
                      )}
                      {a.recommended && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#c6a15b]/15 text-[#8a6d2f]">
                          <Sparkles className="h-3.5 w-3.5" /> {a.recommended}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#c6a15b]" />{a.email}</span>
                      <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#c6a15b]" />{a.phone}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <div className="mt-4 grid sm:grid-cols-5 gap-3 text-sm">
                  <Field k="Goal" v={a.goal} />
                  <Field k="Property" v={a.property} />
                  <Field k="Amount" v={a.amount} />
                  <Field k="Credit" v={a.credit} />
                  <Field k="Timeline" v={a.timeline} />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {["new", "contacted", "approved", "declined"].map((s) => (
                    <button key={s} onClick={() => updateStatus("applications", a.id, s)} className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${a.status === s ? "bg-[#0b1f3a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove("applications", a.id)} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chat Leads */}
        {tab === "chatleads" && (
          <div className="mt-6 space-y-4">
            {chatLeads.length === 0 && <EmptyState label="No chat leads yet. When a visitor gets a recommendation and leaves their details, they'll appear here." />}
            {chatLeads.map((c) => (
              <div key={c.id} className="rounded-2xl bg-white p-6 ring-1 ring-[#c6a15b]/40 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-lg text-[#0b1f3a]">{c.name}</h3>
                      <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-orange-100 text-orange-700">
                        <Flame className="h-3.5 w-3.5" /> Hot Lead
                      </span>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#c6a15b]" />{c.email}</span>
                      {c.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#c6a15b]" />{c.phone}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <p className="mt-4 text-slate-700 bg-slate-50 rounded-lg p-4 whitespace-pre-wrap text-sm">{c.message}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {["new", "contacted", "closed"].map((s) => (
                    <button key={s} onClick={() => updateStatus("contacts", c.id, s)} className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${c.status === s ? "bg-[#0b1f3a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove("contacts", c.id)} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contacts */}
        {tab === "contacts" && (
          <div className="mt-6 space-y-4">
            {messages.length === 0 && <EmptyState label="No messages yet." />}
            {messages.map((c) => (
              <div key={c.id} className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display font-bold text-lg text-[#0b1f3a]">{c.name}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#c6a15b]" />{c.email}</span>
                      {c.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4 text-[#c6a15b]" />{c.phone}</span>}
                      {c.program && <span className="text-slate-500">Interest: {c.program}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <p className="mt-4 text-slate-700 bg-slate-50 rounded-lg p-4">{c.message}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {["new", "contacted", "closed"].map((s) => (
                    <button key={s} onClick={() => updateStatus("contacts", c.id, s)} className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${c.status === s ? "bg-[#0b1f3a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove("contacts", c.id)} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
      <div className="font-medium text-[#0b1f3a] truncate">{v || "—"}</div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-2xl bg-white p-12 text-center ring-1 ring-slate-100">
      <p className="text-slate-500">{label}</p>
    </div>
  );
}
